import { Container } from '@amelio/ui-library';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { getManagerAssessmentStepPath } from '../Evaluation/stepRoutes';
import { isManagerAssessmentSurveySubmitted } from '../Evaluation/submissionState';
import { AssessmentStepSkeleton } from '../shared/AssessmentStepSkeleton/AssessmentStepSkeleton';
import { MeetingCompletionAction } from '../shared/MeetingCompletionAction/MeetingCompletionAction';
import { MeetingTipsAccordion } from '../shared/MeetingTipsAccordion/MeetingTipsAccordion';
import { MeetingTranscriptUpload } from '../shared/MeetingTranscriptUpload/MeetingTranscriptUpload';
import { StepHeader } from '../shared/StepHeader/StepHeader';

import type { EvaluationLocationState, StepDefinition } from '../Evaluation/types';

import { useGetCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import { useAdvanceManagerAssessmentStep } from '@/api/cycle-evaluation-services/manager-assessment';
import { useGetUserById } from '@/api/directory';
import { MeetingAgendaBuilder } from '@/components/MeetingAgendaBuilder/MeetingAgendaBuilder';
import { PlanLockProvider } from '@/components/ProGate/PlanLock';
import { PerformanceProFeature } from '@/constants/PerformanceProFeature';
import { useAssessmentReadOnly } from '@/hooks/useAssessmentReadOnly';
import { useNavigate } from '@/hooks/utils/useNavigate';

export const ManagerAssessmentMeetPage = () => {
    const navigate = useNavigate();
    const location = useLocation<EvaluationLocationState | undefined>();

    const state = location.state;
    const { cycleInstanceId: cycleInstanceIdParam } = useParams<{ cycleInstanceId?: string }>();
    const cycleInstanceId = cycleInstanceIdParam ?? state?.cycleInstanceId;

    const { data: cycleInstance, isLoading } = useGetCycleInstance(cycleInstanceId ?? '');

    const employeeId =
        state?.employeeId ||
        cycleInstance?.reviewAudience?.reviewAudienceAssignments?.[0]?.assignedTo;
    const { data: employeeUser } = useGetUserById({ userId: employeeId ?? '' });

    const rawReviewInstanceId =
        cycleInstance?.reviewInstances?.[0]?.id ??
        cycleInstance?.reviewAnswers?.[0]?.reviewInstanceId;
    const reviewInstanceId =
        rawReviewInstanceId && rawReviewInstanceId !== cycleInstanceId
            ? rawReviewInstanceId
            : undefined;

    const { mutate: advanceManagerAssessmentStep } = useAdvanceManagerAssessmentStep();
    const { isReadOnly, isLockedByPlan } = useAssessmentReadOnly(cycleInstance);

    const hasSubmittedSurvey = isManagerAssessmentSurveySubmitted(cycleInstance);

    // Reaching this page with a submitted survey IS the step-4 (Meet) event. Merely opening Meet
    // (before the meeting is completed) is otherwise never persisted — completing the meeting is what
    // jumps the stepper to 4 — so without this the dashboard stays at 50% and reopens the review on
    // Prepare Meeting. Persist step 4 once per cycle instance; forward-only on the backend,
    // ref-guarded for StrictMode.
    const advancedForCycleInstanceRef = useRef<string | null>(null);
    useEffect(() => {
        if (!cycleInstanceId || advancedForCycleInstanceRef.current === cycleInstanceId) {
            return;
        }
        // A company without Performance PRO only reads its assessments: nothing is saved.
        if (!hasSubmittedSurvey || isLockedByPlan) {
            return;
        }
        advancedForCycleInstanceRef.current = cycleInstanceId;
        advanceManagerAssessmentStep({ cycleInstanceId, step: 4 });
    }, [cycleInstanceId, hasSubmittedSurvey, isLockedByPlan, advanceManagerAssessmentStep]);

    const handleStepClick = useCallback(
        (step: StepDefinition) => {
            navigate({
                path: getManagerAssessmentStepPath(step.id, cycleInstanceId),
                state,
            });
        },
        [navigate, state, cycleInstanceId],
    );

    if (isLoading) {
        return (
            <div data-testid={'manager-assessment-meet-page'}>
                <StepHeader
                    currentStep={4}
                    employeeFirstName={employeeUser?.firstName}
                    onStepClick={handleStepClick}
                />
                <AssessmentStepSkeleton dataTestId={'meet-skeleton'} />
            </div>
        );
    }

    return (
        <PlanLockProvider
            isLocked={isLockedByPlan}
            feature={PerformanceProFeature.ManagerAssessment}
        >
            <div data-testid={'manager-assessment-meet-page'}>
                <StepHeader
                    currentStep={4}
                    employeeFirstName={employeeUser?.firstName}
                    onStepClick={handleStepClick}
                />

                <Container
                    direction={'col'}
                    className={'flex-1'}
                    dataTestId={'meeting-page-content'}
                >
                    <div className={'assessment-journey w-full space-y-4 py-4 md:py-7'}>
                        <MeetingTipsAccordion />

                        <MeetingAgendaBuilder
                            cycleInstanceId={cycleInstanceId || ''}
                            disabled={isReadOnly}
                        />

                        <MeetingTranscriptUpload
                            cycleInstanceId={cycleInstanceId || ''}
                            disabled={isReadOnly}
                        />

                        <MeetingCompletionAction
                            reviewInstanceId={reviewInstanceId || ''}
                            hasSubmittedSurvey={hasSubmittedSurvey}
                            employeeFirstName={employeeUser?.firstName}
                        />
                    </div>
                </Container>
            </div>
        </PlanLockProvider>
    );
};
